import axios from 'axios';
import { http } from '../http';

/**
 * Erro da consulta FIPE com o status HTTP (at-84). O `ModalVeiculos` trata o 404
 * como **lista vazia explicada** — e lia `falha.response.status`, que o `Error`
 * embrulhado aqui nunca tinha: todo 404 virava tela de erro com "Tentar novamente",
 * numa consulta que vai falhar sempre. Com o filtro de 0km o vazio ficou comum.
 */
export class ErroConsultaFipe extends Error {
  constructor(mensagem: string, readonly status?: number) {
    super(mensagem);
    this.name = 'ErroConsultaFipe';
  }
}

const erroFipe = (error: unknown): Error =>
  axios.isAxiosError(error)
    ? new ErroConsultaFipe(error.response?.data?.message || 'Erro ao buscar veículos', error.response?.status)
    : new Error('Erro desconhecido');

/**
 * `zeroKm` (at-84): devolve o preço FIPE **0km** dos modelos que também têm o
 * ano-modelo `year` — ver `VeiculoService.filtroDeAno` no backend.
 */
export const fetchVehicles = async (
  year: number,
  brand: string,
  page: number = 1,
  limit: number = 10,
  zeroKm: boolean = false,
): Promise<any> => {
  try {
    const response = await http.get('/veiculos/listar', {
      params: {
        year,
        brand,
        page,
        limit,
        ...(zeroKm ? { zeroKm: true } : {}),
      },
    });

    return response.data;
  } catch (error: unknown) {
    throw erroFipe(error);
  }
};

/** Estado do espelho FIPE que o SAAC lê (item 14). Espelha o contrato do backend. */
export interface ReferenciaFipe {
  rotulo: string;
  competencia: string;
  quantidade: number;
  percentual: number;
}

export interface StatusEspelhoFipe {
  disponivel: boolean;
  situacao: 'EM_DIA' | 'DEFASADA' | 'PARCIAL' | 'DESCONHECIDA' | 'INDISPONIVEL';
  mensagem: string;
  totalVeiculos?: number;
  referencias?: ReferenciaFipe[];
  predominante?: ReferenciaFipe | null;
  mesesDefasados?: number | null;
  ultimaAtualizacao: string | null;
  ultimaSincronizacao: string | null;
}

/**
 * ⚠️ **Nunca lança** — mesmo desenho do `sgaReconciliacaoApi` (at-32): quem chama é o
 * `PendenciasBanner`, e uma falha aqui não pode derrubar o banner nem virar erro de
 * tela.
 *
 * ⚠️ Falha ao **falar com o SAAC** devolve `DESCONHECIDA`, que o banner ignora (como
 * as outras cinco fontes fazem com o próprio erro). Isso é diferente de
 * `situacao: 'INDISPONIVEL'` **vinda do backend**, que significa "o banco do
 * fipe-atual está fora" — essa é operacional (a seleção de modelo para de funcionar)
 * e precisa acender. Não unificar os dois casos.
 */
export const getFipeStatus = async (): Promise<StatusEspelhoFipe> => {
  try {
    const response = await http.get('/veiculos/fipe-status');
    return response.data;
  } catch {
    return {
      disponivel: false,
      situacao: 'DESCONHECIDA',
      mensagem: 'Não foi possível consultar o estado da base FIPE.',
      ultimaAtualizacao: null,
      ultimaSincronizacao: null,
    };
  }
};

export const searchVehiclesByModel = async (
  model: string,
  year: number,
  brand: string,
  page: number = 1,
  limit: number = 10,
  zeroKm: boolean = false,
): Promise<any> => {
  try {
    const response = await http.get('/veiculos/buscar', {
      params: {
        model,
        year,
        brand,
        page,
        limit,
        ...(zeroKm ? { zeroKm: true } : {}),
      },
    });

    return response.data;
  } catch (error: unknown) {
    throw erroFipe(error);
  }
};
